import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { TextField, MenuItem } from '@mui/material'

import { Pocket } from '../../utils/dexie/db'
import { FormError, SystemUser } from '../../types'
import { UserContext } from '../../contexts/user.context'
import { textFieldWhiteBg } from '../../theme/custom-styles'

const SystemUserDataForm = ({
  errors,
  pockets,
  actionToDo,
  systemUserData,
  selectedPocketId,
  handleSetSystemUserData,
  handlePocketToAssignToUser,
  disableSystemUserRoleSelect
}: {
  errors: FormError
  actionToDo: string
  pockets?: Array<Pocket>
  selectedPocketId: string
  systemUserData: Partial<SystemUser>
  disableSystemUserRoleSelect?: boolean
  handlePocketToAssignToUser: (pocketId: string) => void
  handleSetSystemUserData: (newSystemUserData: SystemUser) => void
}) => {
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['system-users-management'])

  return (
    <div className="system-user-data-form-container">
      <TextField
        fullWidth
        variant="standard"
        sx={textFieldWhiteBg}
        label={t('newSystemUserModal.systemUserDataForm.fields.firstName')}
        error={!!errors.firstName}
        helperText={errors.firstName}
        value={systemUserData.firstName || ''}
        onChange={(event) => {
          const systemUserDataToSet = {
            ...systemUserData,
            firstName: event.target.value.toUpperCase()
          }

          handleSetSystemUserData(systemUserDataToSet as SystemUser)
        }}
      />
      <TextField
        fullWidth
        variant="standard"
        sx={textFieldWhiteBg}
        label={t('newSystemUserModal.systemUserDataForm.fields.lastName')}
        error={!!errors.lastName}
        helperText={errors.lastName}
        value={systemUserData.lastName || ''}
        onChange={(event) => {
          const systemUserDataToSet = {
            ...systemUserData,
            lastName: event.target.value.toUpperCase()
          }

          handleSetSystemUserData(systemUserDataToSet as SystemUser)
        }}
      />
      <TextField
        fullWidth
        variant="standard"
        sx={textFieldWhiteBg}
        label={t('newSystemUserModal.systemUserDataForm.fields.email')}
        disabled={actionToDo === 'update'}
        error={!!errors.username}
        helperText={errors.username}
        value={systemUserData.username || ''}
        onChange={(event) => {
          const systemUserDataToSet = {
            ...systemUserData,
            username: event.target.value.toLowerCase()
          }

          handleSetSystemUserData(systemUserDataToSet as SystemUser)
        }}
      />
      {(actionToDo === 'create' || (actionToDo === 'update' && systemUserData.role?.indexOf('client') === -1)) && (
        <>
          {(userSession?.role === 'admin' || userSession?.role === 'super-admin') && (
            <TextField
              select
              fullWidth
              variant="standard"
              disabled={disableSystemUserRoleSelect}
              label={t('newSystemUserModal.systemUserDataForm.fields.userRole')}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              error={!!errors.role}
              helperText={errors.role}
              value={systemUserData.role || ''}
              onChange={(event) => {
                const systemUserDataToSet = {
                  ...systemUserData,
                  role: event.target.value
                }

                handleSetSystemUserData(systemUserDataToSet as SystemUser)
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.select')}
              </MenuItem>
              {/* <MenuItem value="super-admin-client">{t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.client')}</MenuItem> */}
              <MenuItem value="admin">{t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.administrator')}</MenuItem>
              <MenuItem value="admin-builder">{t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.builder')}</MenuItem>
            </TextField>
          )}
        </>
      )}
      {(userSession?.role === 'admin-client' || userSession?.role === 'super-admin-client') && (
        <>
          <TextField
            select
            fullWidth
            variant="standard"
            label={t('newSystemUserModal.systemUserDataForm.fields.userRole')}
            sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            error={!!errors.role}
            helperText={errors.role}
            value={systemUserData.role || ''}
            onChange={(event) => {
              const systemUserDataToSet = {
                ...systemUserData,
                role: event.target.value
              }

              handleSetSystemUserData(systemUserDataToSet as SystemUser)
            }}
          >
            <MenuItem
              disabled
              value=""
            >
              {t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.select')}
            </MenuItem>
            {userSession?.role === 'super-admin-client' && <MenuItem value="admin-client">{t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.administrator')}</MenuItem>}
            <MenuItem value="admin-client-builder">{t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.builder')}</MenuItem>
          </TextField>
          {userSession?.role === 'super-admin-client' && Array.isArray(pockets) && pockets.length > 1 && (
            <TextField
              select
              fullWidth
              variant="standard"
              label={t('newSystemUserModal.systemUserDataForm.fields.pocketToAssign')}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
              error={!!errors.selectedPocketId}
              helperText={errors.selectedPocketId}
              value={selectedPocketId || ''}
              onChange={(event) => handlePocketToAssignToUser(event.target.value)}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('newSystemUserModal.systemUserDataForm.fields.userRoleSelectOptions.select')}
              </MenuItem>
              {pockets.map((pocketData: Pocket) => {
                return (
                  <MenuItem
                    key={pocketData.id}
                    value={String(pocketData.id)}
                  >
                    {pocketData.name?.toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          )}
        </>
      )}
      <TextField
        fullWidth
        multiline
        maxRows={4}
        variant="standard"
        sx={{
          ...textFieldWhiteBg,
          '& .MuiInputBase-inputMultiline::placeholder': {
            color: '#2ED3b7',
            opacity: '1'
          }
        }}
        value={systemUserData.description || ''}
        onChange={(event) => {
          const systemUserDataToSet = {
            ...systemUserData,
            description: event.target.value
          }

          handleSetSystemUserData(systemUserDataToSet as SystemUser)
        }}
        placeholder={t('newSystemUserModal.systemUserDataForm.fields.description')}
      />
    </div>
  )
}

export default SystemUserDataForm
